import type {
    ICommissionEarning,
    ICommissionEnrollment,
    ICommissionProgramSummary,
    ICommissionWalletSummary,
} from '../types/global/commissionWallet.types';
import { buildCommissionShareUrl } from './commissionShareUrl';

export function formatCommissionUsdCents(cents: number | null | undefined): string {
    if (cents == null || Number.isNaN(Number(cents))) return '—';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(cents) / 100);
}

export function commissionProgramId(program: string | ICommissionProgramSummary | null | undefined): string {
    if (!program) return '';
    if (typeof program === 'string') return program;
    return String(program._id ?? '');
}

export function commissionProgramTitle(program: string | ICommissionProgramSummary | null | undefined): string {
    if (!program || typeof program === 'string') return 'Commission campaign';
    const campaign = program.campaign;
    if (campaign && typeof campaign === 'object' && typeof campaign.title === 'string' && campaign.title.trim()) {
        return campaign.title.trim();
    }
    if (typeof program.title === 'string' && program.title.trim()) return program.title.trim();
    return 'Commission campaign';
}

export function commissionProgramCampaignId(
    program: string | ICommissionProgramSummary | null | undefined,
): string | undefined {
    if (!program || typeof program === 'string') return undefined;
    const c = program.campaign;
    if (typeof c === 'string' && c.trim()) return c.trim();
    if (c && typeof c === 'object' && c._id) return String(c._id);
    return undefined;
}

export interface ICommissionEnrollmentEarningsRow {
    enrollmentId: string;
    programId: string;
    campaignId?: string;
    participantId?: string;
    title: string;
    code: string;
    shareUrl: string | null;
    rate: number | null;
    kind?: string;
    programStatus?: string;
    pendingCents: number;
    clearedCents: number;
    totalEarnedCents: number;
    /** True for app_install / web_visit Circuul programs */
    isAppInstall: boolean;
    /** Cost per install/visit in cents */
    cpaCents?: number;
}

/** Merge approved enrollments with per-program earning totals from wallet payload. */
export function buildCommissionEnrollmentRows(
    wallet: Pick<ICommissionWalletSummary, 'enrollments' | 'earnings'> | null | undefined,
): ICommissionEnrollmentEarningsRow[] {
    if (!wallet?.enrollments?.length) return [];

    const totals = new Map<string, { pendingCents: number; clearedCents: number }>();
    for (const e of wallet.earnings ?? []) {
        const pid = commissionProgramId(e.program);
        if (!pid) continue;
        const cur = totals.get(pid) ?? { pendingCents: 0, clearedCents: 0 };
        const cents = Math.max(0, Math.round(Number(e.commission_cents) || 0));
        if (e.status === 'pending') cur.pendingCents += cents;
        else if (e.status === 'cleared' || e.status === 'paid') cur.clearedCents += cents;
        totals.set(pid, cur);
    }

    return wallet.enrollments.map((en: ICommissionEnrollment) => {
        const program = typeof en.program === 'object' ? en.program : undefined;
        const programId = commissionProgramId(en.program);
        const t = totals.get(programId) ?? { pendingCents: 0, clearedCents: 0 };
        const rate =
            en.commission_rate != null
                ? Number(en.commission_rate)
                : program?.commission_rate != null
                ? Number(program.commission_rate)
                : null;
        const code = String(en.code ?? '').toUpperCase();
        const campaignId = (en.campaign_id && String(en.campaign_id)) || commissionProgramCampaignId(en.program);
        const contestId = program?.contest
            ? typeof program.contest === 'object' && program.contest
                ? String((program.contest as { _id?: string })._id || '')
                : String(program.contest)
            : undefined;
        const contestSlug =
            (en.contest_slug && String(en.contest_slug).trim()) ||
            (program?.contest && typeof program.contest === 'object'
                ? String((program.contest as { slug?: string }).slug || '').trim() || undefined
                : undefined);
        const isAppInstall = program?.kind === 'app_install' || program?.kind === 'web_visit';
        // Prefer participant-scoped rate (en.cpa_cents) over program base rate (program.cpa_cents)
        const cpaCentsRaw =
            typeof en.cpa_cents === 'number' ? en.cpa_cents : program?.cpa_cents;
        const cpaCents =
            typeof cpaCentsRaw === 'number' && !Number.isNaN(cpaCentsRaw) && cpaCentsRaw > 0
                ? cpaCentsRaw
                : undefined;

        return {
            enrollmentId: String(en._id),
            programId,
            campaignId,
            participantId: en.campaign_participant_id ? String(en.campaign_participant_id) : undefined,
            title: commissionProgramTitle(en.program),
            code,
            shareUrl: buildCommissionShareUrl({
                code,
                kind: program?.kind,
                contestSlug,
                contestId,
                campaignId,
                shareUrlFromApi: en.share_url,
            }),
            rate: isAppInstall ? null : (rate != null && !Number.isNaN(rate) ? rate : null),
            kind: program?.kind,
            programStatus: program?.status,
            pendingCents: t.pendingCents,
            clearedCents: t.clearedCents,
            totalEarnedCents: t.pendingCents + t.clearedCents,
            isAppInstall,
            cpaCents,
        };
    });
}

/** Sum commission cents for a given status set (skips voided). */
export function sumCommissionEarningsCents(
    earnings: ICommissionEarning[] | undefined,
    statuses: Array<ICommissionEarning['status']>,
): number {
    if (!earnings?.length) return 0;
    const allowed = new Set(statuses);
    return earnings.reduce((sum, e) => {
        if (!allowed.has(e.status)) return sum;
        return sum + Math.max(0, Math.round(Number(e.commission_cents) || 0));
    }, 0);
}
